// Same-tab memory for the QLink share token (first-experience contract).
//
// store.js moves ?token= out of the URL on entry and keeps it in
// localStorage.userAcquisitiontoken. That key is shared by every link and is
// cleared by several exits (a visit to "/", Start Practicing, /login), after
// which a reload or Back on the stripped URL used to die as "expired". This
// module keeps a per-link copy in sessionStorage (this tab only, never in a
// URL) and puts it back into localStorage when the question page mounts, so
// every existing reader keeps working unchanged. Every access fails soft.
export const QLINK_SHARE_TOKEN_SESSION_PREFIX = "qlearn_qlink_share_token:";
export const QLINK_SHARE_TOKEN_LOCAL_KEY = "userAcquisitiontoken";

const QUESTION_VIEW_PATH = /^\/question\/view\/([^/?#]+)\/?$/;

const safeSession = () => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

const safeLocal = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

const read = (storage, key) => {
  try {
    return storage ? storage.getItem(key) : null;
  } catch (e) {
    return null;
  }
};

const write = (storage, key, value) => {
  try {
    if (storage) storage.setItem(key, value);
  } catch (e) {
    // Best effort: without storage the page behaves as it did before.
  }
};

const remove = (storage, key) => {
  try {
    if (storage) storage.removeItem(key);
  } catch (e) {
    // Best effort, as above.
  }
};

export const qlinkIdFromPath = (pathname) => {
  const match = QUESTION_VIEW_PATH.exec(`${pathname || ""}`);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch (e) {
    // Malformed percent-encoding: this runs inside the fetch interceptor,
    // so it must fail soft rather than break every request.
    return "";
  }
};

export const rememberQLinkShareToken = (qlinkId, token, session = safeSession()) => {
  if (!qlinkId || !token) return;
  write(session, QLINK_SHARE_TOKEN_SESSION_PREFIX + qlinkId, token);
};

// The subject of a QLink share token (a JWT): the link id for public links,
// "<link id>-<user id>" for user links. Read only to tell whose token the
// shared key holds; never trusted for anything else (the server verifies the
// signature). Null for anything unreadable.
const shareTokenSubject = (token) => {
  try {
    const payload = `${token || ""}`.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(window.atob(padded));
    return claims && typeof claims.sub === "string" ? claims.sub : null;
  } catch (e) {
    return null;
  }
};

const isShareTokenFor = (qlinkId, token, tabCopy) => {
  if (!qlinkId || !token) return false;
  if (tabCopy && token === tabCopy) return true;
  const subject = shareTokenSubject(token);
  return subject !== null && (subject === qlinkId || subject.startsWith(`${qlinkId}-`));
};

// A dead link's token is of no further use: drop this tab's copy for the link,
// and the shared key only when it holds THIS link's token (this tab's copy, or
// a token whose subject names the link). The shared key is written by every
// QLink tab, so another live link's token there is left alone.
export const forgetQLinkShareToken = (
  qlinkId,
  { local = safeLocal(), session = safeSession() } = {}
) => {
  if (!qlinkId) return;
  const tabCopy = read(session, QLINK_SHARE_TOKEN_SESSION_PREFIX + qlinkId);
  if (isShareTokenFor(qlinkId, read(local, QLINK_SHARE_TOKEN_LOCAL_KEY), tabCopy)) {
    remove(local, QLINK_SHARE_TOKEN_LOCAL_KEY);
  }
  remove(session, QLINK_SHARE_TOKEN_SESSION_PREFIX + qlinkId);
};

// Returns the share token to use for this link, preferring this tab's copy for
// THIS link over whatever another link left in localStorage.
export const restoreQLinkShareToken = (
  qlinkId,
  { local = safeLocal(), session = safeSession() } = {}
) => {
  const remembered = qlinkId ? read(session, QLINK_SHARE_TOKEN_SESSION_PREFIX + qlinkId) : null;
  if (remembered) {
    write(local, QLINK_SHARE_TOKEN_LOCAL_KEY, remembered);
    return remembered;
  }
  return read(local, QLINK_SHARE_TOKEN_LOCAL_KEY) || null;
};
